/**
 * Accept invitation — the page every invitation email links to
 * (`/accept-invitation?token=…`, plus `&report=1` from the "Report it"
 * footer). It validates the token and routes on the invitation's `kind`:
 *
 *  - activation (org owner): Secure your admin account (password, then 2FA
 *    by authenticator app or secondary email) → recovery codes →
 *    `/onboarding` (Workspace ready); "Set it up later" skips straight there.
 *  - member (invited employee): Create your password → 2FA when the org
 *    requires it (with "Set it up later" while the grace period lasts) →
 *    recovery codes → module tour → the chosen module.
 *  - expired / revoked / used / unknown link → the matching explanation,
 *    with self-service "Send a new link" / "Ask for a new invitation".
 *
 * The page stays mounted after sign-in (main.tsx renders it for this path
 * whether or not a session exists), because 2FA enrolment and the tour run
 * with the session the flow just created. Screens live in
 * modules/onboarding/invitee/.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LoaderCircle, LogIn, UserCheck } from "lucide-react";
import {
  Btn,
  Ic,
  ObApiError,
  ObShell,
  TopBar,
  currentObLocale,
  obErrorCode,
  obToast,
  validateInvitation,
  type InvitationErrorBody,
  type InvitationValidation,
} from "../modules/onboarding/kit";
import {
  EmpTour,
  LINK_CODES,
  LinkProblemScreen,
  MemberMfaScreen,
  MemberPasswordScreen,
  RecoveryScreen,
  ReportScreen,
  SecureAdminScreen,
  StatusCard,
  firstName,
  orgDisplayName,
  type LinkProblemCode,
} from "../modules/onboarding/invitee";
import { useShellStore } from "../store";

type Phase =
  | { name: "validating" }
  | { name: "report" }
  | { name: "link"; code: LinkProblemCode; body: InvitationErrorBody | null }
  | { name: "signin-needed" }
  | { name: "activation" }
  | { name: "activation-recovery"; codes: string[] }
  | { name: "member-password" }
  | { name: "member-mfa" }
  | { name: "member-recovery"; codes: string[] }
  | { name: "member-tour" };

function linkCode(code: string | undefined): LinkProblemCode {
  return code && LINK_CODES.has(code) ? (code as LinkProblemCode) : "invalid";
}

export const AcceptInvitationPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const reportMode = searchParams.get("report") === "1";
  const isAuthenticated = useShellStore((s) => s.isAuthenticated);

  const [phase, setPhase] = useState<Phase>(() =>
    !token ? { name: "link", code: "missing_token", body: null } : reportMode ? { name: "report" } : { name: "validating" },
  );
  const [invitation, setInvitation] = useState<InvitationValidation | null>(null);
  const [personName, setPersonName] = useState("");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!token) {
      setPhase({ name: "link", code: "missing_token", body: null });
      return;
    }
    if (reportMode) {
      setPhase({ name: "report" });
      return;
    }
    let cancelled = false;
    setPhase({ name: "validating" });
    validateInvitation(token)
      .then((data) => {
        if (cancelled) return;
        setInvitation(data);
        setPersonName((data.name ?? "").trim());
        setPhase(data.kind === "activation" ? { name: "activation" } : { name: "member-password" });
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ObApiError && err.status > 0) {
          setPhase({ name: "link", code: linkCode(obErrorCode(err)), body: err.body });
        } else {
          setPhase({ name: "link", code: "network", body: null });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token, reportMode, attempt]);

  const onLinkProblem = useCallback((code: string, body: InvitationErrorBody | null) => {
    setPhase({ name: "link", code: linkCode(code), body });
  }, []);
  const onSignInNeeded = useCallback(() => setPhase({ name: "signin-needed" }), []);

  // Display names follow the active language (TopBar's EN/ع toggle).
  const orgName = invitation
    ? orgDisplayName(invitation.organization, invitation.organization_name, currentObLocale(i18n.language))
    : "";

  switch (phase.name) {
    case "validating":
      return (
        <ObShell>
          <TopBar />
          <main className="ob-wrap ob-anim" style={{ maxWidth: 520, marginTop: "clamp(24px,8vh,80px)", textAlign: "center" }}>
            <p className="ob-p" role="status" style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
              <Ic icon={LoaderCircle} className="ob-spin" color="var(--bl)" />
              {t("onboarding.invitee.checking", "Checking your invitation…")}
            </p>
          </main>
        </ObShell>
      );

    case "report":
      return (
        <ReportScreen
          token={token}
          onSetUp={() => {
            const next = new URLSearchParams(searchParams);
            next.delete("report");
            setSearchParams(next, { replace: true });
          }}
        />
      );

    case "link":
      return (
        <LinkProblemScreen
          key={phase.code}
          code={phase.code}
          token={token}
          body={phase.body}
          authenticated={isAuthenticated}
          onRetry={() => setAttempt((n) => n + 1)}
        />
      );

    case "signin-needed":
      return (
        <ObShell>
          <TopBar orgName={orgName || null} />
          <StatusCard
            tone="ok"
            icon={UserCheck}
            title={t("onboarding.invitee.signInNeeded.title", "Your account is ready.")}
            label="Sign in to continue"
            actions={
              <Btn size="l" icon={LogIn} onClick={() => navigate("/login")}>
                {t("onboarding.invitee.signIn", "Sign in")}
              </Btn>
            }
          >
            <p className="ob-p">
              {t(
                "onboarding.invitee.signInNeeded.body",
                "We couldn't sign you in automatically. Sign in with your new password to finish setting up.",
              )}
            </p>
          </StatusCard>
        </ObShell>
      );

    case "activation":
      return (
        <SecureAdminScreen
          token={token}
          invitation={invitation!}
          onLinkProblem={onLinkProblem}
          onSignInNeeded={onSignInNeeded}
          onSecured={(codes) => setPhase({ name: "activation-recovery", codes })}
          onSkipMfa={() => navigate("/onboarding", { replace: true })}
        />
      );

    case "activation-recovery":
      return (
        <RecoveryScreen
          codes={phase.codes}
          account={invitation!.login_email}
          orgName={orgName}
          onContinue={() => {
            obToast.success(t("onboarding.invitee.secure.securedToast", "Admin account secured"));
            navigate("/onboarding", { replace: true });
          }}
        />
      );

    case "member-password":
      return (
        <MemberPasswordScreen
          token={token}
          invitation={invitation!}
          onLinkProblem={onLinkProblem}
          onSignInNeeded={onSignInNeeded}
          onAccountReady={(name) => {
            if (name) setPersonName(name);
            setPhase(invitation!.mfa?.required ? { name: "member-mfa" } : { name: "member-tour" });
          }}
        />
      );

    case "member-mfa":
      return (
        <MemberMfaScreen
          orgName={orgName}
          personName={personName || null}
          graceDaysLeft={invitation!.mfa?.grace_days_left ?? null}
          secondaryEmailMasked={invitation!.secondary_email_masked ?? null}
          onConfirmed={(codes) => setPhase({ name: "member-recovery", codes })}
          onLater={() => setPhase({ name: "member-tour" })}
        />
      );

    case "member-recovery":
      return (
        <RecoveryScreen
          codes={phase.codes}
          account={invitation!.login_email}
          orgName={orgName}
          personName={personName || null}
          onContinue={() => {
            obToast.success(t("onboarding.invitee.member.securedToast", "Two-step verification is on"));
            setPhase({ name: "member-tour" });
          }}
        />
      );

    case "member-tour":
      return (
        <EmpTour
          orgName={orgName}
          personName={personName || null}
          firstName={firstName(personName)}
          modules={invitation!.modules ?? []}
          onOpen={(path) => navigate(path, { replace: true })}
        />
      );
  }
};

export default AcceptInvitationPage;
